import {
  type HTMLAttributes,
  type ImgHTMLAttributes,
  type ReactNode,
  forwardRef,
  useEffect,
  useState,
} from 'react';

import { RiUserFill } from '@remixicon/react';
import classNames from 'classnames';

import styles from './Avatar.module.css';

export type AvatarVariant = 'circular' | 'rounded' | 'square';

export interface AvatarProps extends HTMLAttributes<HTMLDivElement> {
  src?: string;
  srcSet?: string;
  sizes?: string;
  alt?: string;
  variant?: AvatarVariant;
  imgProps?: ImgHTMLAttributes<HTMLImageElement>;
}

type ImageLoadingStatus = 'pending' | 'loaded' | 'error';

const useImageLoadingStatus = ({
  src,
  srcSet,
  crossOrigin,
  referrerPolicy,
}: {
  src?: string;
  srcSet?: string;
  crossOrigin?: ImgHTMLAttributes<HTMLImageElement>['crossOrigin'];
  referrerPolicy?: ImgHTMLAttributes<HTMLImageElement>['referrerPolicy'];
}): ImageLoadingStatus => {
  const [status, setStatus] = useState<ImageLoadingStatus>('pending');

  useEffect(() => {
    if (!src && !srcSet) {
      return undefined;
    }

    let active = true;
    setStatus('pending');

    const image = new Image();
    image.onload = () => active && setStatus('loaded');
    image.onerror = () => active && setStatus('error');
    if (crossOrigin) {
      image.crossOrigin = crossOrigin;
    }
    if (referrerPolicy) {
      image.referrerPolicy = referrerPolicy;
    }
    if (src) {
      image.src = src;
    }
    if (srcSet) {
      image.srcset = srcSet;
    }

    return () => {
      active = false;
    };
  }, [src, srcSet, crossOrigin, referrerPolicy]);

  return status;
};

export const Avatar = forwardRef<HTMLDivElement, AvatarProps>(
  (
    {
      src,
      srcSet,
      sizes,
      alt,
      variant = 'circular',
      imgProps,
      children,
      className,
      ...rest
    },
    ref,
  ) => {
    const status = useImageLoadingStatus({
      src,
      srcSet,
      crossOrigin: imgProps?.crossOrigin,
      referrerPolicy: imgProps?.referrerPolicy,
    });

    const hasImage = Boolean(src || srcSet);
    const showImage = hasImage && status !== 'error';

    let content: ReactNode;
    if (showImage) {
      const {
        src: _src,
        srcSet: _srcSet,
        sizes: _sizes,
        alt: _alt,
        className: imgClassName,
        ...safeImgProps
      } = imgProps ?? {};

      content = (
        <img
          {...safeImgProps}
          alt={alt}
          src={src}
          srcSet={srcSet}
          sizes={sizes}
          className={classNames(styles.img, imgClassName)}
        />
      );
    } else if (
      children !== null &&
      children !== undefined &&
      children !== false
    ) {
      content = children;
    } else if (hasImage && alt) {
      content = Array.from(alt)[0];
    } else {
      content = (
        <RiUserFill
          className={styles.fallbackIcon}
          aria-hidden
          data-testid="avatar-fallback-icon"
        />
      );
    }

    return (
      <div
        ref={ref}
        className={classNames(styles.root, styles[variant], className)}
        {...rest}
      >
        {content}
      </div>
    );
  },
);

Avatar.displayName = 'Avatar';
